/**
 * TireManagementModal - Hantera däck för ett fordon.
 * Lägg till, redigera, montera, lagra.
 * Stöder per-hjuls mönsterdjup: VF, HF, VB, HB.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XMarkIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { serviceApi, VehicleTire, TireType, TireStorageType } from '@/services/service/serviceApi';

interface TireManagementModalProps {
  vehicleId: number;
  onClose: () => void;
  onUpdated?: () => void;
}

const tireTypeOptions: { value: TireType; labelKey: string; icon: string }[] = [
  { value: 'SUMMER',          labelKey: 'service.tires.summer',          icon: '☀️' },
  { value: 'WINTER_FRICTION', labelKey: 'service.tires.winterFriction',  icon: '❄️' },
  { value: 'WINTER_STUDDED',  labelKey: 'service.tires.winterStudded',   icon: '❄️🔩' },
];

const storageOptions: { value: TireStorageType; labelKey: string }[] = [
  { value: 'HOTEL',   labelKey: 'service.tires.storageHotel' },
  { value: 'STORAGE', labelKey: 'service.tires.storageStorage' },
  { value: 'VEHICLE', labelKey: 'service.tires.storageVehicle' },
  { value: 'OTHER',   labelKey: 'service.tires.storageOther' },
];

function treadColor(mm: number | null): string {
  if (mm == null) return 'text-textTertiary';
  if (mm < 3) return 'text-red-600 font-semibold';
  if (mm < 4) return 'text-amber-600';
  return 'text-green-700';
}

interface TireFormState {
  tireType: TireType;
  brand: string;
  model: string;
  size: string;
  storageType: TireStorageType;
  storageLocation: string;
  treadVf: string;
  treadHf: string;
  treadVb: string;
  treadHb: string;
  notes: string;
}

const emptyForm = (): TireFormState => ({
  tireType: 'SUMMER', brand: '', model: '', size: '',
  storageType: 'STORAGE', storageLocation: '',
  treadVf: '', treadHf: '', treadVb: '', treadHb: '',
  notes: '',
});

const formFromTire = (tire: VehicleTire): TireFormState => ({
  tireType: tire.tireType,
  brand: tire.brand || '',
  model: tire.model || '',
  size: tire.size || '',
  storageType: tire.storageType,
  storageLocation: tire.storageLocation || '',
  treadVf: tire.treadDepthVfMm != null ? String(tire.treadDepthVfMm) : '',
  treadHf: tire.treadDepthHfMm != null ? String(tire.treadDepthHfMm) : '',
  treadVb: tire.treadDepthVbMm != null ? String(tire.treadDepthVbMm) : '',
  treadHb: tire.treadDepthHbMm != null ? String(tire.treadDepthHbMm) : '',
  notes: tire.notes || '',
});

const formToPayload = (f: TireFormState) => {
  const p = (v: string) => v ? parseFloat(v) : undefined;
  const vals = [p(f.treadVf), p(f.treadHf), p(f.treadVb), p(f.treadHb)].filter((v): v is number => v != null);
  const minDepth = vals.length > 0 ? Math.min(...vals) : undefined;
  return {
    tireType: f.tireType,
    brand: f.brand || undefined,
    model: f.model || undefined,
    size: f.size || undefined,
    storageType: f.storageType,
    storageLocation: f.storageLocation || undefined,
    treadDepthVfMm: p(f.treadVf),
    treadDepthHfMm: p(f.treadHf),
    treadDepthVbMm: p(f.treadVb),
    treadDepthHbMm: p(f.treadHb),
    treadDepthMm: minDepth,
    notes: f.notes || undefined,
  };
};

const TireManagementModal: React.FC<TireManagementModalProps> = ({ vehicleId, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const [tires, setTires] = useState<VehicleTire[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTireId, setEditingTireId] = useState<string | null>(null);
  const [form, setForm] = useState<TireFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [newVf, setNewVf] = useState(false);
  const [newHf, setNewHf] = useState(false);
  const [newVb, setNewVb] = useState(false);
  const [newHb, setNewHb] = useState(false);
  const [formError, setFormError] = useState('');
  const downOnBackdrop = useRef(false);
  const formIsOpen = showAddForm || editingTireId != null;

  const loadTires = useCallback(async () => {
    try {
      const data = await serviceApi.getTires(vehicleId);
      setTires(data);
    } catch (err) {
      console.error('Failed to load tires:', err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { loadTires(); }, [loadTires]);

  const setField = (key: keyof TireFormState, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const resetNewFlags = () => {
    setNewVf(false);
    setNewHf(false);
    setNewVb(false);
    setNewHb(false);
    setFormError('');
  };

  const handleAdd = async () => {
    const anyNew = newVf || newHf || newVb || newHb;
    if (anyNew) {
      const required = [
        [newVf, form.treadVf],
        [newHf, form.treadHf],
        [newVb, form.treadVb],
        [newHb, form.treadHb],
      ] as const;
      const missing = required.some(([isNew, value]) => {
        if (!isNew) return false;
        const depth = value ? parseFloat(value) : NaN;
        return Number.isNaN(depth) || depth < 0 || depth > 20;
      });
      if (missing) {
        setFormError(t('service.tires.mountTreadRequired'));
        return;
      }
    }
    setSaving(true);
    setFormError('');
    try {
      await serviceApi.createTire(vehicleId, {
        ...formToPayload(form),
        newTireVf: newVf,
        newTireHf: newHf,
        newTireVb: newVb,
        newTireHb: newHb,
      });
      setShowAddForm(false);
      setForm(emptyForm());
      resetNewFlags();
      await loadTires();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to add tire:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingTireId) return;
    setSaving(true);
    try {
      await serviceApi.updateTire(editingTireId, formToPayload(form));
      setEditingTireId(null);
      setForm(emptyForm());
      await loadTires();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to update tire:', err);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (tire: VehicleTire) => {
    setShowAddForm(false);
    setEditingTireId(tire.id);
    setForm(formFromTire(tire));
  };

  const cancelEdit = () => {
    setEditingTireId(null);
    setForm(emptyForm());
  };

  const handleInstall = async (tireId: string) => {
    try { await serviceApi.installTire(tireId); await loadTires(); onUpdated?.(); }
    catch (err) { console.error('Install failed:', err); }
  };

  const handleStore = async (tireId: string) => {
    try { await serviceApi.storeTire(tireId); await loadTires(); onUpdated?.(); }
    catch (err) { console.error('Store failed:', err); }
  };

  const installed = tires.filter(t => t.status === 'INSTALLED');
  const stored    = tires.filter(t => t.status === 'STORED');
  const other     = tires.filter(t => t.status !== 'INSTALLED' && t.status !== 'STORED');

  const tireTypeIcon  = (type: TireType) => tireTypeOptions.find(o => o.value === type)?.icon || '🛞';
  const tireTypeLabel = (type: TireType) => { const key = tireTypeOptions.find(o => o.value === type)?.labelKey; return key ? t(key) : type; };
  const storageLabel  = (type: TireStorageType) => { const key = storageOptions.find(o => o.value === type)?.labelKey; return key ? t(key) : type; };

  const renderForm = (isAdd: boolean) => (
    <div className={`border rounded-lg p-4 space-y-3 mb-2 ${isAdd ? 'bg-gray-50' : 'bg-blue-50'}`}>
      <h4 className="font-medium text-sm">
        {isAdd ? (t('service.tires.addNew') || 'Nytt däckset') : (t('service.tires.edit') || 'Redigera däck')}
      </h4>

      {/* Typ */}
      <div>
        <label className="text-xs text-textSecondary">{t('service.tires.type') || 'Däcktyp'}</label>
        <select value={form.tireType} onChange={e => setField('tireType', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white">
          {tireTypeOptions.map(o => <option key={o.value} value={o.value}>{o.icon} {t(o.labelKey)}</option>)}
        </select>
      </div>

      {/* Märke + Modell */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-textSecondary">{t('service.tires.brand') || 'Märke'}</label>
          <input type="text" value={form.brand} onChange={e => setField('brand', e.target.value)} placeholder="Michelin" className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white" />
        </div>
        <div>
          <label className="text-xs text-textSecondary">{t('service.tires.model') || 'Modell'}</label>
          <input type="text" value={form.model} onChange={e => setField('model', e.target.value)} placeholder="Pilot Sport" className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white" />
        </div>
      </div>

      {/* Dimension */}
      <div>
        <label className="text-xs text-textSecondary">{t('service.tires.size') || 'Dimension'}</label>
        <input type="text" value={form.size} onChange={e => setField('size', e.target.value)} placeholder="205/55 R16" className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white" />
      </div>

      {isAdd && (
        <div>
          <label className="text-xs text-textSecondary font-medium">{t('service.tires.newTires')}</label>
          <p className="text-xs text-textTertiary mt-0.5 mb-2">{t('service.tires.newTiresHint')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={() => { setNewVf(true); setNewHf(true); setNewVb(true); setNewHb(true); }} className="px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50">{t('service.tires.allFour')}</button>
            <button type="button" onClick={() => { setNewVf(true); setNewHf(true); setNewVb(false); setNewHb(false); }} className="px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50">{t('service.tires.frontAxle')}</button>
            <button type="button" onClick={() => { setNewVf(false); setNewHf(false); setNewVb(true); setNewHb(true); }} className="px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50">{t('service.tires.rearAxle')}</button>
            <button type="button" onClick={resetNewFlags} className="px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50">{t('service.tires.clearNew')}</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {([
              { checked: newVf, set: setNewVf, label: t('service.tires.treadDepthVf') },
              { checked: newHf, set: setNewHf, label: t('service.tires.treadDepthHf') },
              { checked: newVb, set: setNewVb, label: t('service.tires.treadDepthVb') },
              { checked: newHb, set: setNewHb, label: t('service.tires.treadDepthHb') },
            ]).map(pos => (
              <label key={pos.label} className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1.5">
                <input type="checkbox" checked={pos.checked} onChange={e => pos.set(e.target.checked)} />
                {pos.label}
              </label>
            ))}
          </div>
          {formError && <p className="text-xs text-red-600 mt-2">{formError}</p>}
        </div>
      )}

      {/* Mönsterdjup per hjul */}
      <div>
        <label className="text-xs text-textSecondary font-medium">{t('service.tires.treadDepthSection') || 'Mönsterdjup per hjulposition (mm)'}</label>
        <div className="grid grid-cols-4 gap-2 mt-1">
          {[
            { key: 'treadVf' as const, label: t('service.tires.treadDepthVf') || 'VF' },
            { key: 'treadHf' as const, label: t('service.tires.treadDepthHf') || 'HF' },
            { key: 'treadVb' as const, label: t('service.tires.treadDepthVb') || 'VB' },
            { key: 'treadHb' as const, label: t('service.tires.treadDepthHb') || 'HB' },
          ].map(({ key, label }) => (
            <div key={key} className="text-center">
              <label className="text-xs text-textTertiary">{label}</label>
              <input
                type="number" min="0" max="20" step="0.5"
                value={form[key]}
                onChange={e => setField(key, e.target.value)}
                placeholder="–"
                className="w-full mt-1 px-2 py-2 border rounded text-sm bg-white text-center"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-textTertiary mt-1">{t('service.tires.treadDepthAutoMin')}</p>
      </div>

      {/* Förvaring */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-textSecondary">{t('service.tires.storageType') || 'Förvaringstyp'}</label>
          <select value={form.storageType} onChange={e => setField('storageType', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white">
            {storageOptions.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-textSecondary">{t('service.tires.storageLocation') || 'Plats'}</label>
          <input type="text" value={form.storageLocation} onChange={e => setField('storageLocation', e.target.value)} placeholder="BVS Linköping" className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white" />
        </div>
      </div>

      {/* Anteckningar */}
      <div>
        <label className="text-xs text-textSecondary">{t('service.notes') || 'Anteckningar'}</label>
        <input type="text" value={form.notes} onChange={e => setField('notes', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm bg-white" />
      </div>

      <div className="flex gap-2">
        <button
          onClick={isAdd ? handleAdd : handleUpdate}
          disabled={saving}
          className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? '...' : (t('common.save') || 'Spara')}
        </button>
        <button
          onClick={isAdd ? () => { setShowAddForm(false); setForm(emptyForm()); resetNewFlags(); } : cancelEdit}
          className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 bg-white"
        >
          {t('common.cancel') || 'Avbryt'}
        </button>
      </div>
    </div>
  );

  const renderTireCard = (tire: VehicleTire, section: 'installed' | 'stored' | 'other') => {
    if (editingTireId === tire.id) return <div key={tire.id}>{renderForm(false)}</div>;

    const hasPerWheel = tire.treadDepthVfMm != null || tire.treadDepthHfMm != null
      || tire.treadDepthVbMm != null || tire.treadDepthHbMm != null;
    const cells = [
      { label: 'VF', val: tire.treadDepthVfMm },
      { label: 'HF', val: tire.treadDepthHfMm },
      { label: 'VB', val: tire.treadDepthVbMm },
      { label: 'HB', val: tire.treadDepthHbMm },
    ];

    return (
      <div key={tire.id} className={`border rounded-lg p-3 mb-2 ${section === 'other' ? 'opacity-60' : ''}`}>
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span>{tireTypeIcon(tire.tireType)}</span>
              <span className="font-medium text-sm">{tireTypeLabel(tire.tireType)}</span>
              {tire.size && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-textSecondary">{tire.size}</span>}
              {(tire.brand || tire.model) && (
                <span className="text-xs text-textTertiary">{tire.brand} {tire.model}</span>
              )}
            </div>

            {/* Mönsterdjup */}
            {hasPerWheel ? (
              <div className="mt-1.5">
                <div className="grid grid-cols-4 gap-1">
                  {cells.map(({ label, val }) => (
                    <div key={label} className="text-center bg-gray-50 rounded px-1 py-1">
                      <div className="text-xs text-textTertiary">{label}</div>
                      <div className={`text-sm font-medium ${treadColor(val)}`}>
                        {val != null ? val : '–'}
                      </div>
                      {val != null && <div className="text-xs text-textTertiary">mm</div>}
                    </div>
                  ))}
                </div>
              </div>
            ) : tire.treadDepthMm != null ? (
              <div className={`text-xs mt-1 ${treadColor(tire.treadDepthMm)}`}>
                {t('service.tires.treadDepthLabel')}: {tire.treadDepthMm} mm
              </div>
            ) : null}

            {/* Extra info */}
            {section === 'installed' && (tire.lastServiceDate || tire.installedDate) && (
              <div className="text-xs text-textTertiary mt-1">
                🔧 {tire.lastServiceDate || tire.installedDate}
                {tire.installedKm != null && ` · ${tire.installedKm.toLocaleString()} km`}
              </div>
            )}
            {section === 'stored' && (
              <div className="text-xs text-textTertiary mt-1">
                📦 {storageLabel(tire.storageType)}{tire.storageLocation ? `: ${tire.storageLocation}` : ''}
              </div>
            )}
            {tire.notes && <div className="text-xs text-textTertiary italic mt-1">{tire.notes}</div>}
          </div>

          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            <button onClick={() => startEdit(tire)} className="p-1 hover:bg-gray-100 rounded" title={t('common.edit') || 'Redigera'}>
              <PencilIcon className="h-4 w-4 text-textTertiary" />
            </button>
            {section === 'installed' && (
              <button onClick={() => handleStore(tire.id)} className="text-xs px-2 py-1 border rounded hover:bg-gray-50 text-textSecondary whitespace-nowrap">
                {t('service.tires.unmount') || 'Avmontera'}
              </button>
            )}
            {section === 'stored' && (
              <button onClick={() => handleInstall(tire.id)} className="text-xs px-2 py-1 border rounded hover:bg-green-50 text-green-600 border-green-200 whitespace-nowrap">
                {t('service.tires.mount') || 'Montera'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={e => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => {
        if (e.target !== e.currentTarget || !downOnBackdrop.current || formIsOpen) return;
        onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <span className="text-lg">🛞</span>
            {t('service.tires.title') || 'Däck'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setEditingTireId(null);
                if (!showAddForm) {
                  setForm(emptyForm());
                  resetNewFlags();
                  setShowAddForm(true);
                }
              }}
              className="p-1.5 hover:bg-gray-100 rounded border text-sm flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" />
              {t('service.tires.add') || 'Lägg till'}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : (
            <>
              {installed.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-green-600 uppercase mb-2">
                    🟢 {t('service.tires.installed') || 'Monterade'}
                  </h4>
                  {installed.map(tire => renderTireCard(tire, 'installed'))}
                </div>
              )}

              {stored.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-textTertiary uppercase mb-2">
                    ⬜ {t('service.tires.stored') || 'Lagrade'}
                  </h4>
                  {stored.map(tire => renderTireCard(tire, 'stored'))}
                </div>
              )}

              {other.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-textTertiary uppercase mb-2">
                    {t('service.tires.other') || 'Övrigt'}
                  </h4>
                  {other.map(tire => renderTireCard(tire, 'other'))}
                </div>
              )}

              {tires.length === 0 && !showAddForm && (
                <div className="text-center py-8 text-textTertiary text-sm">
                  <div className="text-3xl mb-2">🛞</div>
                  {t('service.tires.none') || 'Inga däck registrerade'}
                  <div className="mt-2">
                    <button onClick={() => setShowAddForm(true)} className="text-primary hover:underline text-sm">
                      {t('service.tires.add') || 'Lägg till'} +
                    </button>
                  </div>
                </div>
              )}

              {showAddForm && renderForm(true)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TireManagementModal;
